import "./SchedulerConfig.scss";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CompanyAPI from "../company/CompanyAPI";
import FloatingVideoButton from "../../commons/buttons/FloatingVideoButton";
import MenuTitle from "../../commons/menu/MenuTitle";
import SchedulerConfigAPI from "./SchedulerConfigAPI";
import SchedulerConfigTimes from "./SchedulerConfigTimes";
import UserService from "../../auth/UserService";
import { displayName } from "../../commons/util/HallAppUtil";

const service = new UserService();
const companyAPI = new CompanyAPI();
const schedulerConfig = new SchedulerConfigAPI();

function SchedulerConfig() {
  const [userLogged, setUserLogged] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [employeeSelected, setEmployeeSelected] = useState(null);

  const [reloadKey, setReloadKey] = useState(0);

  // modal state
  const [showEmpSheet, setShowEmpSheet] = useState(false);
  const [searchEmp, setSearchEmp] = useState("");
  const lastFocusedRef = useRef(null);
  const sheetRef = useRef(null);

  useEffect(() => {
    const user = service.getUserData();
    setUserLogged(user);
  }, []);

  const isAdmin = useMemo(() => {
    return userLogged?.rules?.length > 0 && userLogged.rules.includes("COMPANY");
  }, [userLogged]);

  const getAllEmployee = useCallback(async () => {
    try {
      const companyId = companyAPI.getCompanyManagment().id;
      const userId = service.getUserData().id;

      const response = await schedulerConfig.getEmploees(companyId);

      const employeesNormalized = (response.data || []).map((emp) => {
        const map = {};
        (emp.weeksConfig || []).forEach((w) => {
          map[w.week] = { startTime: w.startTime, endTime: w.endTime };
        });
        return { ...emp, weeksConfigMap: map };
      });

      setEmployees(employeesNormalized);

      const defaultEmp =
        employeesNormalized.find((e) => e.id === userId) || employeesNormalized[0] || null;

      setEmployeeSelected(defaultEmp);
      setReloadKey((k) => k + 1);
    } catch (_) {
      setEmployees([]);
      setEmployeeSelected(null);
    }
  }, []);

  useEffect(() => {
    getAllEmployee();
  }, [getAllEmployee]);

  const openEmpSheet = () => {
    lastFocusedRef.current = document.activeElement;
    setSearchEmp("");
    setShowEmpSheet(true);
  };

  const closeEmpSheet = () => {
    setShowEmpSheet(false);
    setSearchEmp("");
    requestAnimationFrame(() => {
      lastFocusedRef.current?.focus?.();
    });
  };

  const handlePickEmployee = (emp) => {
    setEmployeeSelected(emp);
    setReloadKey((k) => k + 1);
    closeEmpSheet();
  };

  const filteredEmployees = useMemo(() => {
    const s = (searchEmp || "").toLowerCase();
    return (employees || []).filter((e) => {
      const n = (e.name || "").toLowerCase();
      const em = (e.email || "").toLowerCase();
      return n.includes(s) || em.includes(s);
    });
  }, [employees, searchEmp]);

  // acessibilidade: ESC fecha + trava scroll
  useEffect(() => {
    if (!showEmpSheet) return;

    requestAnimationFrame(() => sheetRef.current?.focus?.());

    const onKey = (e) => {
      if (e.key === "Escape") closeEmpSheet();
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [showEmpSheet]);

  const employeeLabel = displayName(employeeSelected?.name) || employeeSelected?.email || "Selecionar";

  return (
    <>
      <div className="container">
        <MenuTitle title="Agenda Semanal" />

        {isAdmin && (
          <div className="ha-empSelect mt-3">
            <div className="ha-empSelect__card">
              <div className="ha-empSelect__top">
                <div className="ha-empSelect__title">Funcionário</div>
              </div>

              {/* Trigger */}
              <button
                type="button"
                className="ha-empSelect__trigger"
                onClick={openEmpSheet}
                aria-haspopup="dialog"
                aria-expanded={showEmpSheet}
              >
                <span className="ha-empSelect__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.42 0-8 2-8 4.5A1.5 1.5 0 0 0 5.5 20h13A1.5 1.5 0 0 0 20 18.5C20 16 16.42 14 12 14Z"
                    />
                  </svg>
                </span>

                <span className="ha-empSelect__value">{employeeLabel}</span>

                <span className="ha-empSelect__chev" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M7 10l5 5 5-5z" />
                  </svg>
                </span>
              </button>

              <div className="ha-empSelect__help">
                Toque para escolher um funcionário e ajustar a agenda semanal.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recarrega ao trocar funcionário */}
      <SchedulerConfigTimes
        fetchAll={() => getAllEmployee()}
        key={reloadKey}
        employeeId={employeeSelected?.id || service.getUserData().id}
      />

      <FloatingVideoButton isSecondFloatingButton={false} youtubeUrl={"https://www.youtube.com/embed/F4fkK58HmoQ"} />

      {/* Bottom Sheet */}
      {showEmpSheet && (
        <div className="haSheet" onClick={(e) => (e.target === e.currentTarget ? closeEmpSheet() : null)}>
          <div
            className="haSheet__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="haEmpSheetTitle"
            tabIndex={-1}
            ref={sheetRef}
          >
            <div className="haSheet__grab" aria-hidden="true" />

            <div className="haSheet__header">
              <div>
                <div id="haEmpSheetTitle" className="haSheet__title">
                  Selecionar funcionário
                </div>
                <div className="haSheet__subtitle">
                  Pesquise pelo nome ou e-mail
                </div>
              </div>

              <button type="button" className="haSheet__close" onClick={closeEmpSheet} aria-label="Fechar">
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <div className="haSheet__body">
              <div className="haSheetSearch">
                <span className="haSheetSearch__icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M10 2a8 8 0 1 0 4.9 14.3l4.4 4.4 1.4-1.4-4.4-4.4A8 8 0 0 0 10 2Zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12Z"
                    />
                  </svg>
                </span>

                <input
                  className="haSheetSearch__input"
                  value={searchEmp}
                  onChange={(e) => setSearchEmp(e.target.value)}
                  placeholder="Pesquisar funcionário..."
                  autoFocus
                />
              </div>

              <div className="haEmpList" role="list">
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((emp) => {
                    const isSelected = employeeSelected?.id === emp.id;
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        className={`haEmpItem ${isSelected ? "is-selected" : ""}`}
                        onClick={() => handlePickEmployee(emp)}
                        role="listitem"
                      >
                        <div className="haEmpItem__main">
                          <div className="haEmpItem__name">{displayName(emp.name) || emp.name}</div>
                          <div className="haEmpItem__email">{emp.email}</div>
                        </div>

                        {isSelected && (
                          <span className="haEmpItem__check" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24">
                              <path fill="currentColor" d="M9 16.2 4.8 12 3.4 13.4 9 19 21 7 19.6 5.6z" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="haEmpEmpty">Nenhum funcionário encontrado.</div>
                )}
              </div>
            </div>

            <div className="haSheet__footer">
              <button type="button" className="haBtn haBtn--ghost" onClick={closeEmpSheet}>
                Fechar
              </button>
            </div>
          </div>

        </div>
      )}
      <div style={{ height: "calc(50px + 72px)" }}></div>
    </>
  );
}

export default SchedulerConfig;
